import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { ArrowRight, Check, CreditCard, Info, Loader2 } from 'lucide-react';

import { useIntl } from '../../util/reactIntl';

import { Page, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { Button } from '../../components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { parse } from '../../util/urlHelpers';
import {
  selectMembership,
  selectMembershipSubscription,
  selectMembershipPrices,
  fetchCurrentUser,
  fetchCurrentUserMembership,
} from '../../ducks/user.duck';
import {
  startBasicMembership,
  startMembershipCheckout,
  openMembershipBillingPortal,
  scheduleMembershipDowngrade,
} from '../../util/api';
import { dismissMembershipOnboarding } from '../../util/supabase';

type Interval = 'month' | 'year';

const BASIC_TIER = 1;
const DEFAULT_SELECTED_TIER = 2;

type Plan = {
  tierNumber: number;
  monthly: { price: string };
  yearly: { price: string };
  featured?: boolean;
  hasBadge?: boolean;
  hasStripeNote?: boolean;
  featureCount: number;
};

const PLANS: Plan[] = [
  {
    tierNumber: 1,
    monthly: { price: '$0' },
    yearly: { price: '$0' },
    featureCount: 5,
  },
  {
    tierNumber: 2,
    monthly: { price: '$49' },
    yearly: { price: '$490' },
    featured: true,
    hasBadge: true,
    hasStripeNote: true,
    featureCount: 9,
  },
  {
    tierNumber: 3,
    monthly: { price: '$149' },
    yearly: { price: '$1,490' },
    hasStripeNote: true,
    featureCount: 9,
  },
];

type PriceCatalogEntry = {
  priceId: string;
  membershipTier: number;
  interval: Interval;
  amountCents: number;
  currency: string;
};

const MembershipPricingPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const intl = useIntl();
  const location = useLocation();
  const scrollingDisabled = useSelector(isScrollingDisabled);
  const membership = useSelector(selectMembership);
  const subscription = useSelector(selectMembershipSubscription);
  const prices = useSelector(selectMembershipPrices) as PriceCatalogEntry[];

  // Query string, not history state: StaticRouter has no state on the server,
  // so reading it there rendered the non-onboarding copy and the first client
  // render swapped the heading, subtitle and skip block underneath it.
  const isOnboarding = parse(location.search).onboarding === 'true';
  const currentInterval = (subscription?.interval ?? null) as Interval | null;
  const currentTierNumber: number = subscription?.membershipTier ?? 0;

  const [interval, setInterval] = useState<Interval>(currentInterval ?? 'month');
  const [selectedPlan, setSelectedPlan] = useState<number>(
    currentTierNumber > 0 ? currentTierNumber : DEFAULT_SELECTED_TIER
  );
  const [busy, setBusy] = useState<number | 'skip' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isAuthenticated = membership !== null;

  const handlePlanCta = async (plan: Plan) => {
    setSelectedPlan(plan.tierNumber);
    setError(null);

    if (!isAuthenticated) {
      history.push({
        pathname: '/login',
        state: { from: { pathname: '/pricing', search: isOnboarding ? '?onboarding=true' : '' } },
      });
      return;
    }

    if (currentTierNumber === plan.tierNumber && currentInterval === interval) return;

    setBusy(plan.tierNumber);
    try {
      const hasActive = !!subscription;
      const targetInterval = plan.tierNumber === BASIC_TIER ? 'month' : interval;
      const targetPrice = prices.find(
        p => p.membershipTier === plan.tierNumber && p.interval === targetInterval
      );
      if (!targetPrice) {
        throw new Error(
          `Stripe price for tier ${plan.tierNumber} ${targetInterval} is not configured.`
        );
      }

      // First-time activation: no existing sub.
      if (!hasActive) {
        if (plan.tierNumber === BASIC_TIER) {
          await startBasicMembership();
          // The endpoint flips postListings inline, but that permission rides on
          // currentUser, so without a refetch the app still routes to
          // /no-posting-rights on a marketplace that would accept the listing.
          await Promise.all([
            dispatch(fetchCurrentUserMembership()),
            dispatch(fetchCurrentUser({ enforce: true })),
          ]);
          history.push(isOnboarding ? '/' : '/account-settings/membership');
          return;
        }
        const { url } = await startMembershipCheckout({
          priceId: targetPrice.priceId,
          returnTo: '/account-settings/membership',
          cancelTo: '/pricing',
        });
        window.location.href = url;
        return;
      }

      // Tier downgrade — schedule the switch at current_period_end so the
      // user keeps current perks until renewal.
      if (plan.tierNumber < currentTierNumber) {
        await scheduleMembershipDowngrade({ priceId: targetPrice.priceId });
        await dispatch(fetchCurrentUserMembership());
        history.push('/account-settings/membership');
        return;
      }

      // Tier upgrade (or same-tier interval change) — deep-link to the
      // Billing Portal confirm step. `always_invoice` on the portal config
      // means Stripe charges proration today and switches immediately.
      const { url } = await openMembershipBillingPortal({
        returnTo: '/account-settings/membership',
        flow: 'subscription_update_confirm',
        priceId: targetPrice.priceId,
      });
      window.location.href = url;
    } catch (e) {
      setError((e as Error).message);
      setBusy(null);
    }
  };

  const handleSkip = async () => {
    setBusy('skip');
    setError(null);
    try {
      await dismissMembershipOnboarding();
      await dispatch(fetchCurrentUserMembership());
      history.push('/');
    } catch (e) {
      setError((e as Error).message);
      setBusy(null);
    }
  };

  const planName = (plan: Plan) =>
    intl.formatMessage({ id: `MembershipPricingPage.tier.${plan.tierNumber}.name` });

  const ctaLabel = (plan: Plan): string => {
    if (currentTierNumber === plan.tierNumber && currentInterval === interval) {
      return intl.formatMessage({ id: 'MembershipPricingPage.currentPlan' });
    }
    if (!subscription) {
      return intl.formatMessage({ id: `MembershipPricingPage.tier.${plan.tierNumber}.cta` });
    }
    if (plan.tierNumber > currentTierNumber) {
      return intl.formatMessage(
        { id: 'MembershipPricingPage.upgradeTo' },
        { name: planName(plan) }
      );
    }
    if (plan.tierNumber < currentTierNumber) {
      return intl.formatMessage(
        { id: 'MembershipPricingPage.switchTo' },
        { name: planName(plan) }
      );
    }
    return intl.formatMessage(
      { id: 'MembershipPricingPage.switchToCadence' },
      { name: planName(plan), interval }
    );
  };

  return (
    <Page
      title={intl.formatMessage({ id: 'MembershipPricingPage.pageTitle' })}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className="flex w-full flex-grow flex-col bg-[#fcfaf7]">
          <main className="mx-auto flex w-full max-w-7xl flex-grow flex-col px-4 py-8 md:px-6 md:py-10 lg:py-8">
            <section className="rounded-[18px] border border-[#ece6dc] bg-white px-5 py-8 shadow-[0_24px_70px_-50px_rgba(15,23,42,0.22)] md:px-8 md:py-9 lg:px-10 lg:py-8">
              <div className="mx-auto max-w-3xl text-center">
                <h1 className="text-3xl font-heading font-bold uppercase leading-[0.95] text-foreground md:text-5xl">
                  {intl.formatMessage({
                    id: isOnboarding
                      ? 'MembershipPricingPage.titleOnboarding'
                      : 'MembershipPricingPage.title',
                  })}
                </h1>
                <p className="mx-auto mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground md:text-base">
                  {intl.formatMessage({
                    id: isOnboarding
                      ? 'MembershipPricingPage.subtitleOnboarding'
                      : 'MembershipPricingPage.subtitle',
                  })}
                </p>
                <p className="mx-auto mt-1 max-w-3xl text-sm italic leading-relaxed text-muted-foreground md:text-base">
                  {intl.formatMessage({ id: 'MembershipPricingPage.feeNote' })}
                </p>
              </div>

              <div className="mt-6 flex items-center justify-center">
                <div className="inline-flex rounded-full border border-[#ece6dc] bg-[#f7f1e8] p-1">
                  <button
                    type="button"
                    onClick={() => setInterval('month')}
                    className={`rounded-full px-4 py-1.5 text-xs font-heading font-bold uppercase tracking-wide transition-colors ${
                      interval === 'month' ? 'bg-foreground text-background' : 'text-muted-foreground'
                    }`}
                  >
                    {intl.formatMessage({ id: 'MembershipPricingPage.intervalMonthly' })}
                  </button>
                  <button
                    type="button"
                    onClick={() => setInterval('year')}
                    className={`rounded-full px-4 py-1.5 text-xs font-heading font-bold uppercase tracking-wide transition-colors ${
                      interval === 'year' ? 'bg-foreground text-background' : 'text-muted-foreground'
                    }`}
                  >
                    {intl.formatMessage({ id: 'MembershipPricingPage.intervalYearly' })}
                  </button>
                </div>
              </div>

              <div className="mt-8 grid gap-5 lg:grid-cols-3">
                {PLANS.map(plan => {
                  const isFeatured = !!plan.featured;
                  const isSelected = selectedPlan === plan.tierNumber;
                  const isCurrent =
                    currentTierNumber === plan.tierNumber && currentInterval === interval;
                  const isBusy = busy === plan.tierNumber;
                  const displayPrice = interval === 'year' ? plan.yearly : plan.monthly;

                  return (
                    <article
                      key={plan.tierNumber}
                      className={`relative flex cursor-pointer flex-col rounded-[14px] border p-5 transition-colors ${
                        isSelected
                          ? 'border-primary bg-[#fffaf5] shadow-[0_24px_60px_-42px_rgba(255,122,0,0.28)]'
                          : isFeatured
                          ? 'border-[#171513] bg-[#fcfbf9] shadow-[0_24px_60px_-42px_rgba(23,21,19,0.2)]'
                          : 'border-[#e9e2d8] bg-white shadow-[0_18px_50px_-42px_rgba(15,23,42,0.14)]'
                      }`}
                      onClick={() => setSelectedPlan(plan.tierNumber)}
                    >
                      <div className="mb-4 flex h-6 items-center">
                        {plan.hasBadge ? (
                          <div className="w-fit rounded-sm bg-primary px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-primary-foreground">
                            {intl.formatMessage({ id: 'MembershipPricingPage.badgeMostPopular' })}
                          </div>
                        ) : (
                          <div className="h-6" aria-hidden="true" />
                        )}
                      </div>

                      <h2 className="text-[2rem] font-heading font-bold uppercase leading-none text-foreground">
                        {planName(plan)}
                      </h2>

                      <div className="mt-5 flex items-end gap-2">
                        <span className="text-5xl font-bold leading-none text-foreground">{displayPrice.price}</span>
                        <span className="pb-1 text-sm font-medium text-muted-foreground">
                          {intl.formatMessage({
                            id:
                              interval === 'year'
                                ? 'MembershipPricingPage.cadenceYear'
                                : 'MembershipPricingPage.cadenceMonth',
                          })}
                        </span>
                      </div>

                      <p className="mt-4 min-h-[4.5rem] text-sm leading-relaxed text-muted-foreground">
                        {intl.formatMessage({
                          id: `MembershipPricingPage.tier.${plan.tierNumber}.description`,
                        })}
                      </p>

                      <div
                        className={`mt-5 min-h-[5.9rem] rounded-[12px] border px-4 py-3 ${
                          isSelected
                            ? 'border-primary/20 bg-primary/5'
                            : isFeatured
                            ? 'border-[#171513]/10 bg-[#171513]/[0.03]'
                            : 'border-[#eadfce] bg-[#fbf7f2]'
                        }`}
                      >
                        <div className="flex min-h-[4.3rem] items-start gap-3">
                          <span
                            className={`mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${
                              isSelected
                                ? 'bg-primary text-primary-foreground'
                                : isFeatured
                                ? 'bg-[#171513] text-white'
                                : 'bg-white text-primary shadow-sm'
                            }`}
                          >
                            <CreditCard className="h-4 w-4" />
                          </span>
                          <div className="flex min-h-[4.3rem] flex-col justify-start">
                            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground">
                              {intl.formatMessage({ id: 'MembershipPricingPage.successFeeLabel' })}
                            </p>
                            <p className="mt-1 text-sm font-heading font-bold uppercase text-foreground">
                              {intl.formatMessage({
                                id: `MembershipPricingPage.tier.${plan.tierNumber}.saleFee`,
                              })}
                            </p>
                            <p
                              className={`mt-1 text-xs font-medium text-muted-foreground ${
                                plan.hasStripeNote ? '' : 'invisible'
                              }`}
                              aria-hidden={!plan.hasStripeNote}
                            >
                              {plan.hasStripeNote
                                ? intl.formatMessage({
                                    id: `MembershipPricingPage.tier.${plan.tierNumber}.stripeNote`,
                                  })
                                : '\u00A0'}
                            </p>
                          </div>
                        </div>
                      </div>

                      <Button
                        size="sm"
                        variant={isSelected ? 'default' : 'outline'}
                        disabled={isCurrent || !!busy}
                        className={`mt-5 h-10 w-full rounded-md font-heading text-sm uppercase ${
                          isSelected
                            ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                            : 'border-[#cfc4b5] bg-transparent text-foreground hover:bg-[#f7f1e8]'
                        }`}
                        onClick={event => {
                          event.stopPropagation();
                          handlePlanCta(plan);
                        }}
                      >
                        {isBusy ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            {ctaLabel(plan)}
                            {!isCurrent && <ArrowRight className="ml-2 h-4 w-4" />}
                          </>
                        )}
                      </Button>

                      <ul className="mt-4 space-y-2">
                        {Array.from({ length: plan.featureCount }, (_, idx) => idx + 1)
                          .filter(n => {
                            // Feature keys have no en.json fallback. Presence in
                            // intl.messages means the operator added the copy in
                            // Sharetribe Console. Deleting the Console entry
                            // removes the feature from the pricing card.
                            const key = `MembershipPricingPage.tier.${plan.tierNumber}.feature.${n}`;
                            const msg = intl.messages[key];
                            return typeof msg === 'string' && msg.trim().length > 0;
                          })
                          .map(n => {
                            const tooltipKey = `MembershipPricingPage.tier.${plan.tierNumber}.feature.${n}.tooltip`;
                            const hasTooltip =
                              typeof intl.messages[tooltipKey] === 'string' &&
                              (intl.messages[tooltipKey] as string).trim().length > 0;
                            // Operator sets this Console key with any non-empty
                            // value (e.g. "1") to flag the feature as not yet
                            // shipped. Missing / empty = shipped.
                            const comingSoonKey = `MembershipPricingPage.tier.${plan.tierNumber}.feature.${n}.comingSoon`;
                            const isComingSoon =
                              typeof intl.messages[comingSoonKey] === 'string' &&
                              (intl.messages[comingSoonKey] as string).trim().length > 0;
                            return (
                              <li
                                key={n}
                                className="flex items-start gap-2.5 text-xs text-foreground"
                              >
                                <span
                                  className={`mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full ${
                                    isSelected
                                      ? 'bg-primary text-primary-foreground'
                                      : isFeatured
                                      ? 'bg-[#171513] text-white'
                                      : 'bg-primary/10 text-primary'
                                  }`}
                                >
                                  <Check className="h-3 w-3" />
                                </span>
                                <span className="leading-snug">
                                  {intl.formatMessage({
                                    id: `MembershipPricingPage.tier.${plan.tierNumber}.feature.${n}`,
                                  })}
                                  {isComingSoon ? (
                                    <span className="ml-1.5 inline-block rounded-sm bg-[#fef3c7] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-[#92400e] align-middle">
                                      {intl.formatMessage({
                                        id: 'MembershipPricingPage.comingSoon',
                                      })}
                                    </span>
                                  ) : null}
                                  {hasTooltip ? (
                                    <Popover>
                                      <PopoverTrigger asChild>
                                        <button
                                          type="button"
                                          onClick={e => e.stopPropagation()}
                                          aria-label={intl.formatMessage({
                                            id: 'MembershipPricingPage.featureMoreInfo',
                                          })}
                                          className="ml-1.5 inline-flex h-6 w-6 items-center justify-center rounded-full align-middle text-muted-foreground hover:text-foreground"
                                        >
                                          <Info
                                            className="h-5 w-5"
                                            strokeWidth={2}
                                            fill="none"
                                          />
                                        </button>
                                      </PopoverTrigger>
                                      <PopoverContent
                                        side="top"
                                        align="start"
                                        className="max-w-xs text-xs"
                                        onClick={e => e.stopPropagation()}
                                      >
                                        {intl.formatMessage({ id: tooltipKey })}
                                      </PopoverContent>
                                    </Popover>
                                  ) : null}
                                </span>
                              </li>
                            );
                          })}
                      </ul>
                    </article>
                  );
                })}
              </div>

              {error && <p className="mt-6 text-center text-sm text-destructive">{error}</p>}

              {isOnboarding && (
                <div className="mt-8 text-center">
                  <button
                    type="button"
                    onClick={handleSkip}
                    disabled={!!busy}
                    className="text-sm text-muted-foreground underline-offset-4 hover:underline disabled:opacity-50"
                  >
                    {intl.formatMessage({
                      id:
                        busy === 'skip'
                          ? 'MembershipPricingPage.skipForNowSaving'
                          : 'MembershipPricingPage.skipForNow',
                    })}
                  </button>
                </div>
              )}
            </section>
          </main>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

export default MembershipPricingPage;
